import React, { useEffect, useState } from 'react';
import Sidebar from '../../components/Sidebar';
import '../CompanyHistory.css';
import { db } from '../../firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';

// Document Center Component
const DownloadCenter = ({ activeTab }) => {
  const [posts, setPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const postsPerPage = 5;

  // Load posts from Firebase in real-time
  useEffect(() => {
    const postsRef = collection(db, 'en-posts');
    const q = query(postsRef, orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const postsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPosts(postsData);
    });

    // Unsubscribe when component unmounts
    return () => unsubscribe();
  }, []);

  // Reset search term when tab changes
  useEffect(() => {
    setSearchTerm('');
    setCurrentPage(1);
  }, [activeTab]);

  // Filter data according to tab
  const filteredPosts = posts.filter(post => {
    // Category filtering
    const categoryMatch = activeTab === 'catalog' ? 
      (post.category === 'catalog' || !post.category) : 
      (post.category === 'technical');
    
    // Search term filtering
    const searchMatch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       post.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    return categoryMatch && searchMatch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const currentPosts = filteredPosts.slice(startIndex, startIndex + postsPerPage);

  // PDF download
  const handleDownload = (post) => {
    if (post.pdfUrl) {
      window.open(post.pdfUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (post.pdfFile) {
      const link = document.createElement('a');
      link.href = post.pdfFile;
      link.download = post.pdfFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Show popup and increase view count when post is clicked
  const handlePostClick = async (post) => {
    setSelectedPost(post);
    setShowPopup(true);
    
    // Increase view count
    try {
      const { doc, updateDoc, increment } = await import('firebase/firestore');
      const postRef = doc(db, 'en-posts', post.id);
      await updateDoc(postRef, {
        viewCount: increment(1)
      });
    } catch (error) {
      console.error('Failed to increase view count:', error);
    }
  };

  // Close popup
  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedPost(null);
  };

  // Change page
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getCenterInfo = () => {
    if (activeTab === 'catalog') {
      return {
        icon: '📋',
        title: 'Product Catalog',
        subtitle: 'Product catalog of Taebong Industrial Technology Co., Ltd.'
      };
    } else {
      return {
        icon: '📚',
        title: 'Technical Documentation',
        subtitle: 'Technical documents and manuals of Taebong Industrial Technology Co., Ltd.'
      };
    }
  };

  const centerInfo = getCenterInfo();

  return (
    <div className="board-container">
      {/* Board Header */}
      <div className="board-header">
        <h2>{centerInfo.icon} {centerInfo.title}</h2>
        <p>{centerInfo.subtitle}</p>
      </div>

      {/* Post List */}
      <div className="board-content">
        <div className="board-table">
          <table>
            <thead>
              <tr>
                <th className="col-number">No.</th>
                <th className="col-title">Title</th>
                <th className="col-author">Author</th>
                <th className="col-date">Date</th>
                <th className="col-views">Views</th>
              </tr>
            </thead>
            <tbody>
              {currentPosts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="empty-message">
                    <div className="empty-state">
                      <span>📄</span>
                      <p>No documents registered.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                currentPosts.map((post, index) => (
                  <tr key={post.id} onClick={() => handlePostClick(post)} className="post-row">
                    <td className="post-number">{startIndex + index + 1}</td>
                    <td className="post-title">
                      <span className="title-text">{post.title?.replace(/[^\w\s가-힣]/g, '') || 'No Title'}</span>
                    </td>
                    <td className="post-author">Administrator</td>
                    <td className="post-date">
                      {post.createdAt ? 
                        new Date(post.createdAt.seconds ? post.createdAt.seconds * 1000 : post.createdAt).toLocaleDateString('en-US') : 
                        'No Date'
                      }
                    </td>
                    <td className="post-views">{post.viewCount || 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="board-pagination">
            <div className="pagination-info">
              Total {filteredPosts.length} documents
            </div>
            <div className="pagination-buttons">
              <button 
                className="page-btn prev-btn"
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const startPage = Math.max(1, currentPage - 2);
                const page = startPage + i;
                if (page > totalPages) return null;
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`page-btn ${currentPage === page ? 'active' : ''}`}
                  >
                    {page}
                  </button>
                );
              })}
              <button 
                className="page-btn next-btn"
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Search Area */}
      <div className="board-search">
        <div className="search-container">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search for document title or content..."
            className="search-input"
          />
          <button className="search-btn">
            <span>🔍</span>
            Search
          </button>
        </div>
      </div>

      {/* Post Detail Popup */}
      {showPopup && selectedPost && (
        <div className="post-popup-overlay" onClick={handleClosePopup}>
          <div className="post-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>{selectedPost.title?.replace(/[^\w\s가-힣]/g, '') || 'No Title'}</h3>
              <button className="close-btn" onClick={handleClosePopup}>
                ✕
              </button>
            </div>
            <div className="popup-content">
              <div className="post-meta">
                <span className="meta-item">
                  <strong>Author:</strong> Administrator
                </span>
                <span className="meta-item">
                  <strong>Date:</strong> {
                    selectedPost.createdAt ? 
                      new Date(selectedPost.createdAt.seconds ? selectedPost.createdAt.seconds * 1000 : selectedPost.createdAt).toLocaleDateString('en-US') : 
                      'No Date'
                  }
                </span>
                <span className="meta-item">
                  <strong>Views:</strong> {selectedPost.viewCount || 0}
                </span>
              </div>
              <div className="post-body">
                <p>{selectedPost.content}</p>
              </div>
              {selectedPost.pdfFileName && (
                <div className="popup-actions">
                  <button 
                    onClick={() => handleDownload(selectedPost)}
                    className="download-btn"
                  >
                    📄 Download {selectedPost.pdfFileName}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CustomerSupport = () => {
  // URL 해시가 있으면 해당 섹션을 기본값으로 설정
  const getInitialSection = () => {
    const hash = window.location.hash.replace('#', '');
    return hash && ['customer-center', 'online-quote', 'download-center'].includes(hash) 
      ? hash 
      : 'customer-center';
  };
  
  const [activeSection, setActiveSection] = useState(getInitialSection);
  const [activeTab, setActiveTab] = useState('catalog');
  
  const sidebarItems = [
    { name: 'Customer Center', path: 'customer-center' },
    { name: 'Online Quote', path: 'online-quote' },
    { name: 'Document Center', path: 'download-center' }
  ];

  // Scroll spy function
  useEffect(() => {
    const handleScroll = () => {
      const sections = sidebarItems.map(item => item.path);
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle hash URL (move to corresponding section when page loads)
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && ['customer-center', 'online-quote', 'download-center'].includes(hash)) {
      setActiveSection(hash);
      // 여러 번 시도하여 확실하게 처리
      const attemptScroll = (attempts = 0) => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else if (attempts < 5) {
          setTimeout(() => attemptScroll(attempts + 1), 200);
        }
      };
      setTimeout(() => attemptScroll(), 100);
    }
  }, []);

  // 페이지 로드 완료 후 해시 처리 (추가 보장)
  useEffect(() => {
    const handleLoad = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && ['customer-center', 'online-quote', 'download-center'].includes(hash)) {
        setActiveSection(hash);
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300);
      }
    };

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
      return () => window.removeEventListener('load', handleLoad);
    }
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'auto' });
    }
  };

  // Handle quote request form submission
  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    
    // Combine email address
    const email = `${data.emailUser}@${data.emailDomain}`;
    
    // Send data to server
    try {
      // Set API URL according to environment
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? 'https://us-central1-taibong-hp.cloudfunctions.net/sendQuote'  // Firebase Functions URL
        : 'http://localhost:5000/api/send-quote';  // Local development server
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company: data.company,
          name: data.name,
          telephone: data.telephone,
          projectName: data.projectName,
          serviceInProcess: data.serviceInProcess,
          email: email,
          hotFluidName: data.hotFluidName,
          coldFluidName: data.coldFluidName,
          hotFlowRate: data.hotFlowRate,
          coldFlowRate: data.coldFlowRate,
          flowUnit: data.flowUnit,
          hotTempIn: data.hotTempIn,
          coldTempIn: data.coldTempIn,
          tempUnit: data.tempUnit,
          hotTempOut: data.hotTempOut,
          coldTempOut: data.coldTempOut,
          tempOutUnit: data.tempOutUnit,
          hotPressureDrop: data.hotPressureDrop,
          coldPressureDrop: data.coldPressureDrop,
          pressureUnit: data.pressureUnit,
          hotDesignPressure: data.hotDesignPressure,
          coldDesignPressure: data.coldDesignPressure,
          designPressureUnit: data.designPressureUnit,
          hotDesignTemp: data.hotDesignTemp,
          coldDesignTemp: data.coldDesignTemp,
          designTempUnit: data.designTempUnit,
          plateMaterial: data.plateMaterial,
          gasketMaterial: data.gasketMaterial,
          comment: data.comment
        })
      });

      const result = await response.json();

      if (result.success) {
        alert('✅ Quote request has been sent to saledept@taibong.com!\n\nPlease check your email address to receive a reply.');
        e.target.reset();
      } else {
        alert(`❌ Failed to send quote request: ${result.message}`);
      }
    } catch (error) {
      console.error('Quote request submission error:', error);
      alert('❌ Server connection error: Please check if the server is running.\n\nServer startup method:\n1. cd new_project/server\n2. npm start');
    }
  };

  return (
    <>
      <Sidebar 
        category="Customer Support" 
        items={sidebarItems} 
        activeSection={activeSection}
        onItemClick={scrollToSection}
      />
      <div className="company-history-page">
        <div className="history-container">
          <h1 className="page-category-title">Customer Support</h1>
          
          {/* Customer Center Section */}
          <section id="customer-center" className="section">
            <h2 className="section-title">Customer Center</h2>
            <div className="section-container">
              <div className="contact-offices">
                {/* Incheon Head Office */}
                <div className="office-card">
                  <h3 className="office-title">Incheon Head Office</h3>
                  <div className="contact-info">
                    <div className="contact-item">
                      <span className="contact-label">Address:</span>
                      <span className="contact-value">327 Namdongdong-ro, Namdong-gu, Incheon (Namdong Industrial Complex 43B-9L)</span>
                    </div>
                    <div className="contact-item">
                      <span className="contact-label">Tel:</span>
                      <span className="contact-value">032-811-0511</span>
                    </div>
                    <div className="contact-item">
                      <span className="contact-label">Email:</span>
                      <span className="contact-value">saledept@taibong.com</span>
                    </div>
                  </div>
                  <div className="office-image-container">
                    <img src="/com_incheon.png" alt="Incheon Head Office" className="office-image" />
                  </div>
                </div>

                {/* Southern Branch */}
                <div className="office-card">
                  <h3 className="office-title">Southern Branch</h3>
                  <div className="contact-info">
                    <div className="contact-item">
                      <span className="contact-label">Address:</span>
                      <span className="contact-value">968 Haenghae-eup, Buk-gu, Pohang-si, Gyeongsangbuk-do, Ensol 1st Floor</span>
                    </div>
                    <div className="contact-item">
                      <span className="contact-label">Tel:</span>
                      <span className="contact-value">054-231-0513</span>
                    </div>
                    <div className="contact-item">
                      <span className="contact-label">Email:</span>
                      <span className="contact-value">shlee1@taibong.com</span>
                    </div>
                  </div>
                  <div className="office-image-container">
                    <img src="/com_pohang.png" alt="Southern Branch" className="office-image" />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Online Quote Section */}
          <section id="online-quote" className="section">
            <h2 className="section-title">Online Quote</h2>
            <div className="section-container">
              <div className="quote-form-container">
                <div className="form-header">
                  <div className="form-icon">→</div>
                  <h3 className="form-title">QUESTIONNAIRE for PLATE HEAT EXCHANGER</h3>
                </div>
                
                <form className="quote-form" onSubmit={handleQuoteSubmit}>
                  {/* Company and Contact Information */}
                  <div className="form-section">
                    <div className="form-row">
                      <div className="form-field">
                        <label className="field-label">Company</label>
                        <input type="text" name="company" className="field-input" required />
                      </div>
                      <div className="form-field">
                        <label className="field-label">Name</label>
                        <input type="text" name="name" className="field-input" required />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-field">
                        <label className="field-label">Telephone</label>
                        <input type="tel" name="telephone" className="field-input" required />
                      </div>
                      <div className="form-field">
                        <label className="field-label">Project Name</label>
                        <input type="text" name="projectName" className="field-input" required />
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-field">
                        <label className="field-label">Service in Process</label>
                        <input type="text" name="serviceInProcess" className="field-input" required />
                      </div>
                      <div className="form-field email-field">
                        <label className="field-label">E-mail</label>
                        <div className="email-inputs">
                          <input type="text" name="emailUser" className="email-input" required />
                          <span className="email-at">@</span>
                          <input type="text" name="emailDomain" className="email-input" required />
                          <select name="emailDomainSelect" className="email-domain-select" onChange={(e) => {
                            const domainInput = e.target.parentElement.querySelector('input[name="emailDomain"]');
                            if (e.target.value && e.target.value !== '') {
                              domainInput.value = e.target.value;
                            } else {
                              domainInput.value = '';
                            }
                          }}>
                            <option value="">Direct Input</option>
                            <option value="gmail.com">gmail.com</option>
                            <option value="naver.com">naver.com</option>
                            <option value="daum.net">daum.net</option>
                            <option value="nate.com">nate.com</option>
                            <option value="yahoo.com">yahoo.com</option>
                            <option value="hotmail.com">hotmail.com</option>
                            <option value="outlook.com">outlook.com</option>
                            <option value="hanmail.net">hanmail.net</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Fluid Specifications */}
                  <div className="form-section">
                    <h4 className="section-subtitle">Details (if available)</h4>
                    <div className="spec-table">
                      <div className="table-header">
                        <div className="table-cell header-cell"></div>
                        <div className="table-cell header-cell">HOT FLUID</div>
                        <div className="table-cell header-cell">COLD FLUID</div>
                        <div className="table-cell header-cell">UNIT</div>
                      </div>
                      
                      <div className="table-row">
                        <div className="table-cell label-cell">Fluid Name</div>
                        <div className="table-cell"><input type="text" name="hotFluidName" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="coldFluidName" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="fluidUnit" className="table-input" /></div>
                      </div>
                      
                      <div className="table-row">
                        <div className="table-cell label-cell">Flow Rate(Kg/Hr)</div>
                        <div className="table-cell"><input type="text" name="hotFlowRate" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="coldFlowRate" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="flowUnit" className="table-input" /></div>
                      </div>
                      
                      <div className="table-row">
                        <div className="table-cell label-cell">Temperature(In)(°C)</div>
                        <div className="table-cell"><input type="text" name="hotTempIn" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="coldTempIn" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="tempUnit" className="table-input" /></div>
                      </div>
                      
                      <div className="table-row">
                        <div className="table-cell label-cell">Temperature(Out)(°C)</div>
                        <div className="table-cell"><input type="text" name="hotTempOut" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="coldTempOut" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="tempOutUnit" className="table-input" /></div>
                      </div>
                      
                      <div className="table-row">
                        <div className="table-cell label-cell">Pressure Drop(Kg/cm²)</div>
                        <div className="table-cell"><input type="text" name="hotPressureDrop" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="coldPressureDrop" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="pressureUnit" className="table-input" /></div>
                      </div>
                      
                      <div className="table-row">
                        <div className="table-cell label-cell">Design Pressure(Kg/cm²)</div>
                        <div className="table-cell"><input type="text" name="hotDesignPressure" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="coldDesignPressure" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="designPressureUnit" className="table-input" /></div>
                      </div>
                      
                      <div className="table-row">
                        <div className="table-cell label-cell">Design Temperature(°C)</div>
                        <div className="table-cell"><input type="text" name="hotDesignTemp" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="coldDesignTemp" className="table-input" /></div>
                        <div className="table-cell"><input type="text" name="designTempUnit" className="table-input" /></div>
                      </div>
                      
                      <div className="table-row">
                        <div className="table-cell label-cell">Plate Material</div>
                        <div className="table-cell">
                          <select name="plateMaterial" className="table-select">
                            <option value="">-- Select --</option>
                            <option value="SS304">SS304</option>
                            <option value="SS316">SS316</option>
                            <option value="SS316L">SS316L</option>
                            <option value="Hastelloy C">Hastelloy C</option>
                            <option value="Ti">Ti</option>
                            <option value="SM0254">SM0254</option>
                            <option value="904L">904L</option>
                          </select>
                        </div>
                        <div className="table-cell"></div>
                        <div className="table-cell"></div>
                      </div>
                      
                      <div className="table-row">
                        <div className="table-cell label-cell">Gasket Material</div>
                        <div className="table-cell">
                          <select name="gasketMaterial" className="table-select">
                            <option value="">-- Select --</option>
                            <option value="NBR">NBR</option>
                            <option value="EPDM">EPDM</option>
                            <option value="VITON">VITON</option>
                            <option value="H-NBR">H-NBR</option>
                            <option value="SILICONE">SILICONE</option>
                            <option value="PTFE">PTFE</option>
                          </select>
                        </div>
                        <div className="table-cell"></div>
                        <div className="table-cell"></div>
                      </div>
                    </div>
                  </div>

                  {/* Comment */}
                  <div className="form-section">
                    <h4 className="section-subtitle">COMMENT</h4>
                    <textarea name="comment" className="comment-textarea" rows="4" placeholder="Please enter additional comments."></textarea>
                  </div>

                  {/* Submit Button */}
                  <div className="form-actions">
                    <button type="submit" className="submit-btn">Request Quote</button>
                  </div>
                </form>
              </div>
            </div>
          </section>

          {/* Document Center Section */}
          <section id="download-center" className="section">
            <h2 className="section-title">Document Center</h2>
            <div className="section-container">
              {/* Document Center Button Section */}
              <div className="download-center-buttons">
                <button 
                  className={`download-center-btn ${activeTab === 'catalog' ? 'active' : ''}`}
                  onClick={() => setActiveTab('catalog')}
                >
                  <span className="btn-icon">📋</span>
                  <span className="btn-text">Product Catalog</span>
                </button>
                <button 
                  className={`download-center-btn ${activeTab === 'technical' ? 'active' : ''}`}
                  onClick={() => setActiveTab('technical')}
                >
                  <span className="btn-icon">📚</span>
                  <span className="btn-text">Technical Documentation</span>
                </button>
              </div>
              
              <DownloadCenter activeTab={activeTab} />
            </div>
          </section>

        </div>
      </div>
    </>
  );
};

export default CustomerSupport;